import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
  Update,
} from '@reduxjs/toolkit'
import { getImports } from './api'
import {
  defaultFilters,
  IImport,
  ImportPageState,
  ImportStatus,
} from './typings'
import { StoreData } from '../reducers/typings'
import { updateFilterState } from './helpers'
import { ImportFilterSet } from './filters'
import { loadFilterPreferences } from '../userPreferences'
import { deserializeFilters } from '@user/components/Filters/utils/deserializeFilters'

export const importsAdapter = createEntityAdapter<IImport>()

export const initialState = importsAdapter.getInitialState<ImportPageState>({
  page: 1,
  perPage: 10,
  count: 0,
  loading: true,
  shouldFetchList: false,
  filterSet: defaultFilters,
})

const localSelectors = importsAdapter.getSelectors()

export const selectImportPageState = (state: StoreData) => {
  const {
    imports: { page, perPage, count, loading, shouldFetchList, filterSet },
  } = state

  return {
    page,
    pages: Math.ceil(count / perPage),
    perPage,
    count,
    loading,
    shouldFetchList,
    filterSet,
  }
}

export const importsSlice = createSlice({
  name: 'imports',
  initialState,
  reducers: {
    setProgress: (
      state,
      {
        payload,
      }: PayloadAction<{
        importId: string
        percentage: number
        status: ImportStatus
        totalRecords: number
      }>,
    ) => {
      const id = localSelectors
        .selectAll(state)
        .find((x) => x.importId === payload.importId)?.id

      if (id) {
        const update: Update<IImport> = {
          id: id,
          changes: {
            progressPercentage: payload.percentage,
            status: payload.status,
            totalRecords: payload.totalRecords,
          },
        }

        importsAdapter.updateOne(state, update)
      }
    },

    setImportFilters: (state, { payload }: PayloadAction<ImportFilterSet>) =>
      updateFilterState(state, payload),

    setImportsPage: (state, { payload: page }: PayloadAction<number>) => {
      state.page = page > 0 ? page : 1
      state.shouldFetchList = true
    },

    setImportsPerPage: (state, { payload }: PayloadAction<number>) => {
      state.page = 1
      state.perPage = payload
      state.shouldFetchList = true
    },

    setAll: (state, { payload }: PayloadAction<IImport[]>) => {
      importsAdapter.setAll(state, payload)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getImports.pending, (state) => {
      state.loading = true
      state.shouldFetchList = false
    })

    builder.addCase(getImports.rejected, (state) => {
      state.loading = false
      state.count = 0
      state.page = 1
      importsAdapter.removeAll(state)
    })

    builder.addCase(getImports.fulfilled, (state, { payload }) => {
      const { '@odata.count': count, value } = payload
      state.loading = false
      if (count !== undefined) {
        state.count = count
      }
      // correct dates
      value.map((i) => {
        if (i.createdDate !== null) i.createdDate = new Date(i.createdDate)
      })
      importsAdapter.setAll(state, value)
    })

    builder.addCase(loadFilterPreferences.fulfilled, (state, { payload }) => {
      const importPref = payload.find((p) => p.type === 'import')
      return updateFilterState(
        state,
        !!importPref
          ? deserializeFilters<IImport, ImportFilterSet>(
              defaultFilters,
              importPref.filter,
            )
          : defaultFilters,
      )
    })
  },
})

export default importsSlice.reducer

export const importAdapterSelectors = importsAdapter.getSelectors<StoreData>(
  (state) => state.imports,
)

export const selectImportFilters = (state: StoreData) => {
  const {
    imports: { filterSet, page, perPage },
  } = state
  return { filterSet, page, perPage }
}

export const {
  selectById: selectImportById,
  selectIds: selectImportIds,
  selectAll: selectAllImports,
  selectTotal: selectTotalImports,
} = importAdapterSelectors

export const {
  setAll,
  setProgress,
  setImportFilters,
  setImportsPage,
  setImportsPerPage,
} = importsSlice.actions
